import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeftLong } from "react-icons/fa6";
import { IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Swal from "sweetalert2";

import ReusableMuiTable from "../../../components/common/Table/Table";
import Button from "../../../components/common/Button/Button";
import { showToast } from "../../../components/common/ToastNotification/ToastNotification";
import {
  getCustomGalleryId,
  getCustomDeleteGallery,
} from "../../../api/service/custompage.service";

const CustomGalleryList = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(false);


  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);


  const fetchGallery = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getCustomGalleryId(id, page);
    
      if (Array.isArray(response?.data?.data)) {
        setGalleries(response.data.data);
        setTotalCount(response.data.total || 0);
      } else {
        setGalleries([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error(error);
      showToast.error("Failed to fetch gallery");
      setGalleries([]);
    } finally {
      setLoading(false);
    }
  }, [id, page]);

  useEffect(() => {
    if (id) fetchGallery();
  }, [fetchGallery, id]);


  const handleDelete = (galleryId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This gallery item will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d32f2f",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await getCustomDeleteGallery(galleryId);
          showToast.success("Custom Gallery deleted successfully");
          fetchGallery();
        } catch (error) {
          console.error(error);
          showToast.error("Failed to delete gallery");
        }
      }
    });
  };


  const columns = [
    {
      name: "title",
      label: "Title",
      options: {
        sort: true,
        customBodyRender: (_, tableMeta) => {
          const gallery = galleries[tableMeta.rowIndex];
          return gallery?.title || "N/A";
        },
      },
    },
    {
      name: "category",
      label: "Category",
      options: {
        sort: true,
        customBodyRender: (_, tableMeta) => {
          const gallery = galleries[tableMeta.rowIndex];
          return gallery?.category || "N/A";
        },
      },
    },
    {
      name: "description",
      label: "Description",
      options: {
        sort: false,
        customBodyRender: (value) => (
          <div
            style={{
              maxWidth: "300px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {value || "N/A"}
          </div>
        ),
      },
    },
    {
      name: "action",
      label: "Action",
      options: {
        sort: false,
        customBodyRender: (_, tableMeta) => {
          const gallery = galleries[tableMeta.rowIndex];
          return (
            <div style={{ display: "flex", gap: "8px" }}>
              <Tooltip title="Edit">
                <IconButton
                  onClick={() =>
                    navigate(`/update-custom-gallery/${gallery.id}`)
                  }
                  sx={{
                    color: "#C39B22",
                    "&:hover": { backgroundColor: "rgba(195,155,34,0.1)" },
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Delete">
                <IconButton
                  onClick={() => handleDelete(gallery.id)}
                  sx={{
                    color: "#d32f2f",
                    "&:hover": { backgroundColor: "rgba(211,47,47,0.1)" },
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </div>
          );
        },
      },
    },
  ];

  return (
    <>

      <div className="d-flex gap-2 align-items-center justify-content-between mb-3">
        <div className="d-flex gap-2 align-items-center">
          <FaArrowLeftLong
            style={{ cursor: "pointer" }}
            onClick={() => navigate(-1)}

          />
          <h3 className="management-tile mb-0 ml-2">
            Gallery List
          </h3>
        </div>

        <Button
          onClick={() => navigate(`/add-custom-gallery/${id}`)}
          style={{
            backgroundColor: "#C39B22",
            color: "#ffffff",
          }}
        >
          Add Gallery
        </Button>
      </div>


      <ReusableMuiTable
        title=""
        data={galleries}
        columns={columns}
        loading={loading}
        options={{
          serverSide: true,
          selectableRows: "none",
          search: false,
          count: totalCount,
          page: page - 1,
          rowsPerPage: rowsPerPage,
          onChangePage: (currentPage) =>
            setPage(currentPage + 1),
          textLabels: {
            body: {
              noMatch: loading ? "Loading..." : "No gallery found",
            },
          },
        }}
      />
    </>
  );
};

export default CustomGalleryList;
